// src/admin/pages/ProductList.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrashAlt, FaSearch, FaBoxOpen, FaFilter } from 'react-icons/fa';
import Notification from '../components/Notification/Notification';

// --- CONFIGURATION ---
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://www.ayushvatika.com/api';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || 'https://www.ayushvatika.com/images/products/';
// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8484/api';
// const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || 'http://localhost:8484/images/products/';

// Auth Helpers
const getAuthToken = (): string | null => localStorage.getItem('authToken');
const getAuthHeaders = () => {
  const token = getAuthToken();
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  return headers;
};

// Interfaces
interface Category {
  id: number;
  name: string;
  parentId?: number;
}
interface CategoryType {
  id: number;
  name: string;
}
interface Product {
  id: number;
  productName: string;
  imageUrl?: string;
  price: number;
  stockQuantity: number;
  status: string;
  category: Category;
  categoryType?: CategoryType;
  parentCategoryName?: string;
}

const ProductList: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info'; } | null>(null);

  // Filter & Pagination State
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [sortColumn, setSortColumn] = useState<string>('productName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [productsPerPage] = useState<number>(10);
  
  // Modal State
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // --- Fetch Data ---
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const headers = getAuthHeaders();

    try {
      const [productsResponse, categoriesResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/products`, { headers }),
        fetch(`${API_BASE_URL}/categories`, { headers })
      ]);

      if (productsResponse.status === 401 || productsResponse.status === 403) {
          showNotification('Session expired. Redirecting...', 'error');
          navigate('/admin/login');
          return;
      }

      if (!productsResponse.ok || !categoriesResponse.ok) throw new Error('Failed to fetch data');

      const productsData: Product[] = await productsResponse.json();
      const categoriesData: Category[] = await categoriesResponse.json();
      setCategories(categoriesData);

      // Map Parent Categories
      const categoryParents: { [key: number]: string } = {};
      await Promise.all(categoriesData.map(async (category) => {
        if (category.parentId && !categoryParents[category.parentId]) {
          const parentRes = await fetch(`${API_BASE_URL}/categories/${category.parentId}`, { headers });
          if (parentRes.ok) {
            const parentCat: Category = await parentRes.json();
            categoryParents[parentCat.id] = parentCat.name;
          }
        }
      }));

      const formattedProducts = productsData.map(product => {
        const productCategory = categoriesData.find(cat => cat.id === product.category.id);
        const parentCategoryName = productCategory?.parentId ? categoryParents[productCategory.parentId] : '';
        
        return {
          ...product,
          status: product.stockQuantity > 10 ? 'In Stock' : product.stockQuantity > 0 ? 'Low Stock' : 'Out of Stock',
          parentCategoryName: parentCategoryName,
          category: productCategory || product.category
        };
      });

      setProducts(formattedProducts);
    } catch (err: any) {
      showNotification(`Error: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // --- Filter & Sort Logic ---
  const filteredAndSortedProducts = useMemo(() => {
    let currentProducts = [...products];

    // Filter
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      currentProducts = currentProducts.filter(p =>
        p.productName.toLowerCase().includes(lower) ||
        (p.category?.name && p.category.name.toLowerCase().includes(lower)) ||
        p.status.toLowerCase().includes(lower)
      );
    }
    if (filterCategory !== 'all') {
      const catId = parseInt(filterCategory);
      currentProducts = currentProducts.filter(p => p.category && p.category.id === catId);
    }

    // Sort
    if (sortColumn) {
      currentProducts.sort((a, b) => {
        let valA: any, valB: any;
        if (sortColumn === 'categoryName') {
          valA = a.category?.name.toLowerCase() || '';
          valB = b.category?.name.toLowerCase() || '';
        } else if (sortColumn === 'status') {
             const statusOrder: any = { 'in stock': 1, 'low stock': 2, 'out of stock': 3 };
             valA = statusOrder[a.status.toLowerCase()] || 99;
             valB = statusOrder[b.status.toLowerCase()] || 99;
        } else {
             valA = (a as any)[sortColumn];
             valB = (b as any)[sortColumn];
        }

        if (typeof valA === 'string') return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        return sortOrder === 'asc' ? valA - valB : valB - valA;
      });
    }
    return currentProducts;
  }, [products, searchTerm, filterCategory, sortColumn, sortOrder]);

  // Pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProductsForDisplay = filteredAndSortedProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredAndSortedProducts.length / productsPerPage);

  const paginate = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) setCurrentPage(pageNumber);
  };

  // Actions
  const initiateDelete = (product: Product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (productToDelete) {
      const headers = getAuthHeaders();
      try {
        const response = await fetch(`${API_BASE_URL}/products/${productToDelete.id}`, { method: 'DELETE', headers });
        if (!response.ok) throw new Error('Delete failed');
        
        showNotification('Product deleted successfully!', 'success');
        setShowDeleteModal(false);
        setProductToDelete(null);
        fetchProducts();
      } catch (err: any) {
        showNotification(`Failed to delete: ${err.message}`, 'error');
      }
    }
  };

  const handleEditProduct = (id: number) => {
    navigate(`/admin/products/edit/${id}`); 
  };

  // Styles (Updated for Light/Dark Mode)
  const inputStyle = "w-full p-2.5 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors bg-white border-gray-300 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400";
  const selectStyle = "p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors bg-white border-gray-300 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white";

  return (
    <AdminLayout>
      {/* Background: Dynamic Light/Dark */}
      <div className="min-h-screen p-6 md:p-8 font-sans transition-colors duration-300 bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <h1 className="text-3xl font-bold flex items-center text-gray-800 dark:text-white">
             <FaBoxOpen className="mr-3 text-green-600 dark:text-green-500" /> Product List
          </h1>
          <button
            onClick={() => navigate('/admin/product/add')}
            className="px-6 py-2.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center justify-center font-semibold shadow-md"
          >
            <FaPlus className="mr-2" /> Create Product
          </button>
        </div>

        {/* Filters */}
        <div className="p-5 rounded-xl shadow-lg border mb-6 bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
           <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
              
              <div className="relative w-full md:w-1/3">
                <input
                  type="text"
                  placeholder="Search products..."
                  className={inputStyle}
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>

              <div className="flex flex-wrap gap-4 w-full md:w-auto justify-end">
                <div className="flex items-center gap-2">
                    <FaFilter className="text-gray-500 dark:text-gray-400" />
                    <select
                    value={filterCategory}
                    onChange={(e) => { setFilterCategory(e.target.value); setCurrentPage(1); }}
                    className={selectStyle}
                    >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                    </select>
                </div>

                <select
                  value={sortColumn}
                  onChange={(e) => {
                    const col = e.target.value;
                    setSortColumn(col);
                    setSortOrder(col === sortColumn ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'asc');
                    setCurrentPage(1);
                  }}
                  className={selectStyle}
                >
                  <option value="productName">Name {sortColumn === 'productName' && (sortOrder === 'asc' ? '↑' : '↓')}</option>
                  <option value="price">Price {sortColumn === 'price' && (sortOrder === 'asc' ? '↑' : '↓')}</option>
                  <option value="stockQuantity">Stock {sortColumn === 'stockQuantity' && (sortOrder === 'asc' ? '↑' : '↓')}</option>
                  <option value="categoryName">Category {sortColumn === 'categoryName' && (sortOrder === 'asc' ? '↑' : '↓')}</option>
                  <option value="status">Status {sortColumn === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}</option>
                </select>
              </div>
           </div>
        </div>

        {notification && <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}

        {/* Table */}
        {loading ? (
           <div className="text-center py-10 text-gray-500 dark:text-gray-400">Loading products...</div>
        ) : filteredAndSortedProducts.length === 0 ? (
           <div className="text-center py-10 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              No products found.
           </div>
        ) : (
          <>
            <div className="rounded-xl shadow-lg border overflow-hidden bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">Image</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">Product Name</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">Category</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">Price</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">Stock</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">Status</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {currentProductsForDisplay.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                            {product.imageUrl ? (
                            <img src={`${IMAGE_BASE_URL}${product.imageUrl}`} alt={product.productName} className="h-10 w-10 rounded-lg object-cover border border-gray-300 dark:border-gray-600" onError={(e) => { e.currentTarget.src = 'https://placehold.co/40x40/374151/FFFFFF?text=IMG'; }} />
                            ) : (
                            <div className="h-10 w-10 rounded-lg flex items-center justify-center text-xs border bg-gray-100 border-gray-300 text-gray-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400">No Img</div>
                            )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{product.productName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                            <span className="block">{product.category.name}</span>
                            {product.parentCategoryName && <span className="text-xs text-gray-500">{product.parentCategoryName}</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600 dark:text-green-400">₹{product.price.toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{product.stockQuantity}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${
                                product.status === 'In Stock' ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' :
                                product.status === 'Low Stock' ? 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800' :
                                'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
                            }`}>
                                {product.status}
                            </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                            <button onClick={() => handleEditProduct(product.id)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mr-4 transition-colors p-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded">
                                <FaEdit size={16} />
                            </button>
                            <button onClick={() => initiateDelete(product)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors p-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded">
                                <FaTrashAlt size={16} />
                            </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex justify-center items-center gap-2 mt-6">
                <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors bg-white border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                    Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                    <button
                        key={i + 1}
                        onClick={() => paginate(i + 1)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                            currentPage === i + 1 
                            ? 'bg-green-600 text-white border-green-600' 
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'
                        }`}
                    >
                        {i + 1}
                    </button>
                ))}
                <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors bg-white border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                    Next
                </button>
            </div>
          </>
        )}

        {/* Delete Modal */}
        {showDeleteModal && productToDelete && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-gray-700 transform transition-all scale-100">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Confirm Deletion</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-6">
                        Are you sure you want to delete product: <span className="font-semibold text-red-600 dark:text-red-400 block mt-1 text-lg">{productToDelete.productName}</span>
                    </p>
                    <div className="flex justify-end space-x-4">
                        <button
                            onClick={() => setShowDeleteModal(false)}
                            className="px-6 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-semibold"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={confirmDelete}
                            className="px-6 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors font-semibold shadow-lg"
                        >
                            Delete
                        </button>
                    </div>
                </div>
            </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ProductList;